// ============================================================
// reservationChatPusher — 自社チャット（LP）のリアルタイム接続（2026-10-11）
//
// LP（new-n-car）の Pusher アプリに **2 本目の接続**を張り、`private-admin-chat` を購読する。
// r-cal の pusherService（店舗の dispatch / line-chat アプリ）とは別インスタンス。
//   - 那覇: 同じアプリだが、宮古の r-cal は別アプリなので店舗に関係なくこちらで繋ぐ
//   - 購読認証: chat.php?action=pusher_auth に Bearer（r-cal のログイントークン）。LP が backend /auth/me で検証
//   - key は公開値（instances.ts chatPusherKey）。secret はサーバーのみ
//   - 受けたイベントは useReservationChat の共有一覧に反映（applyReservationChatRealtime）
//   - 失敗（購読エラー・接続エラー）は logEvent。接続状態は hook の realtimeConnected に出し、ポーリング間隔が切り替わる
// 設計: md/master_md/page-line-chat.md「リアルタイム」
// ============================================================
import Pusher from 'pusher-js';
import { isTrainingMode } from '../training/trainingMode';
import { getReservationChatApiUrl, getReservationChatPusherConfig, isReservationChatEnabled } from '../config/backend';
import { getAuthToken } from '../api/dispatchClient';
import { logEvent } from '../lib/eventLog';
import { RESV_CHAT_ADMIN_CHANNEL, buildChatChannelAuthorization } from '../lib/reservationChatRealtime';
import {
  applyReservationChatRealtime,
  fetchReservationChatSessions,
  setReservationChatRealtimeConnected,
} from '../hooks/useReservationChat';

const readToken = (): string | null => {
  const t = getAuthToken();
  if (t) return t;
  try {
    return window.localStorage.getItem('authToken');
  } catch {
    return null;
  }
};

class ReservationChatPusher {
  pusher: any = null;
  channel: any = null;
  subscribed = false;
  hasConnectedOnce = false;

  connect(): void {
    if (this.pusher) return;
    if (!isReservationChatEnabled()) return;
    const { key, cluster } = getReservationChatPusherConfig();
    const apiUrl = getReservationChatApiUrl();
    const token = readToken();
    if (!key || !cluster || !apiUrl) {
      console.warn('[ResvChatPusher] LP の Pusher 設定が無いため接続しません（ポーリングで動作）');
      return;
    }
    const channelAuthorization = buildChatChannelAuthorization(apiUrl, token);
    if (!channelAuthorization) {
      // 未ログイン: private は購読できない。ログイン後に connect() し直す
      logEvent({ action: 'resv_chat.pusher.connect', result: 'fail', err: 'no auth token' });
      return;
    }

    this.pusher = new Pusher(key, { cluster, forceTLS: true, channelAuthorization });

    this.pusher.connection.bind('connected', () => {
      const isReconnect = this.hasConnectedOnce;
      this.hasConnectedOnce = true;
      console.log('[ResvChatPusher] connected', isReconnect ? '(reconnect)' : '(initial)');
      // 切断中に取りこぼした分は一覧を取り直す（開いているパネルは次のポーリングで追従）
      if (isReconnect) fetchReservationChatSessions();
      if (this.subscribed) setReservationChatRealtimeConnected(true);
    });
    this.pusher.connection.bind('disconnected', () => {
      setReservationChatRealtimeConnected(false);
    });
    this.pusher.connection.bind('unavailable', () => {
      setReservationChatRealtimeConnected(false);
    });
    this.pusher.connection.bind('error', (err: any) => {
      // 接続エラー（ネットワーク・over quota 等）。ポーリングが継続するので致命ではないが、記録は残す
      logEvent({ action: 'resv_chat.pusher.connection', result: 'fail', err: err?.error?.data?.message ?? err?.message ?? err });
    });

    this.channel = this.pusher.subscribe(RESV_CHAT_ADMIN_CHANNEL);
    this.channel.bind('pusher:subscription_succeeded', () => {
      this.subscribed = true;
      setReservationChatRealtimeConnected(true);
      console.log('[ResvChatPusher] subscribed', RESV_CHAT_ADMIN_CHANNEL);
    });
    this.channel.bind('pusher:subscription_error', (err: any) => {
      this.subscribed = false;
      setReservationChatRealtimeConnected(false);
      const status = err?.status ?? err?.data?.status ?? null;
      logEvent({
        action: 'resv_chat.pusher.subscribe',
        target: RESV_CHAT_ADMIN_CHANNEL,
        result: 'fail',
        detail: { status },
        err: err?.error ?? err?.message ?? err,
      });
    });
    this.channel.bind_global((eventName: string, data: any) => {
      if (eventName.startsWith('pusher:')) return;
      if (isTrainingMode()) return; // 本番の更新を練習画面に混ぜない（pusherService と同じ）
      try {
        applyReservationChatRealtime(eventName, data);
      } catch (e) {
        logEvent({ action: 'resv_chat.pusher.apply', target: eventName, result: 'fail', err: e });
      }
    });
  }

  disconnect(): void {
    if (!this.pusher) return;
    try {
      this.pusher.unsubscribe(RESV_CHAT_ADMIN_CHANNEL);
      this.pusher.disconnect();
    } catch { /* ignore */ }
    this.pusher = null;
    this.channel = null;
    this.subscribed = false;
    setReservationChatRealtimeConnected(false);
  }
}

export const reservationChatPusher = new ReservationChatPusher();

if (typeof window !== 'undefined') {
  (window as any).__resvChatPusher = reservationChatPusher;
}
